import { Localized, useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import { ButtonGroup, Dialog } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../app/LoadingContainer"
import { loadAllUserRequests, UserRequests } from "./userRequestsApi"
import { UserRequestsStatusChip } from "./UserRequestsStatusChip"
import { If } from "../../app/If"
import produce from "immer"
import { AddEditUserRequestDialog, AddEditUserRequestDialogProps } from "./dialogs/AddEditUserRequestDialog"
import { UserRequestFilters, UserRequestsFilterPanel } from "./UserRequestsFilterPanel"
import { defUserRequestsFilters } from "./helper"
import { ErrorContainer } from "../../app/ErrorContainer"

type UserRequestDialogState =
    | { type: "none" }
    | { type: "adminEditUserRequest", props: AddEditUserRequestDialogProps }
    | { type: "readUserRequest", props: AddEditUserRequestDialogProps }

export const UserRequestsList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const [userRequests, setUserRequests] = useState<UserRequests[]>([]);
    const [userRequestsFiltered, setUserRequestsFiltered] = useState<UserRequests[]>([]);
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState<UserRequestFilters>(defUserRequestsFilters);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadAllUserRequests()).then(unwrapResult).then(res => {
            setUserRequests(res);
            setUserRequestsFiltered(res);
        })
        .catch((error) => { setIsError(true); setError(error); })
        .finally(() => setIsLoading(false));;
    }, [dispatch]);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const [dialog, setDialog] = useState<UserRequestDialogState>({ type: "none" })

    const reload = () => {
        const selectedFilters = filters.statusOptions.filter(filter => filter.selected).map(filter => filter.uniqueName);
        if(selectedFilters.length === 0) setUserRequestsFiltered(userRequests);
        else setUserRequestsFiltered(userRequests.filter(f => selectedFilters.includes(f.status)));
    }

    const handleEditUserRequest = useCallback((userRequest: UserRequests) => {
        setDialog({
            type: "adminEditUserRequest",
            props: {
                userRequest: userRequest,
                mode: "create",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const handleReadUserRequest = useCallback((userRequest: UserRequests) => {
        setDialog({
            type: "readUserRequest",
            props: {
                userRequest: userRequest,
                mode: "create",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const DialogContentDispatcher = ({ state, setter }: { state: UserRequestDialogState, setter: Dispatch<SetStateAction<UserRequestDialogState>> }) => {
        const dispatch = useAppDispatch()

        const onSuccess = useCallback((userRequest: UserRequests | number) => {
            setter({ type: "none" })
            setUserRequests(
                produce((draft) => {
                    if (draft !== undefined && typeof(userRequest) !== "number") {
                        const existingIndex = draft.findIndex((group) => group.id === userRequest.id);

                        if (existingIndex !== -1) {
                            draft[existingIndex] = { ...draft[existingIndex], ...userRequest };
                        } else {
                            draft.push(userRequest);
                        }
                    }
                })
            );
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "adminEditUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={state.props.userRequest}
                    mode={'adminEdit'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />
            case "readUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={state.props.userRequest}
                    mode={'read'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const columns: GridColDef<UserRequests, any, any>[] = [
        {
            field: 'userId',
            headerName: l10n.getString("username", null, "username"),
            width: 300
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            width: 300,
            valueGetter: (value) => {
                return new Date(value).toLocaleString();
            }
        },
        {
            field: "actionUser",
            headerName: "Administrator",
            flex: 1
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => {
                return <UserRequestsStatusChip status={params.row.status}/>
            }
        },
        {
            field: 'actions', type: 'actions', width: 150,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <If condition={params.row.status === "NEW"} otherwise={<Button
                            style={{ width: 100 }}
                            onClick={() => handleReadUserRequest(params.row)}
                            color="secondary"
                            variant="outlined" >
                            <Localized id="details">
                                <span>_Szczegóły</span>
                            </Localized>
                        </Button>}>
                            <Button
                                style={{ width: 100 }}
                                onClick={() => handleEditUserRequest(params.row)}
                                color="secondary"
                                variant="outlined" >
                                <Localized id="edit">
                                    <span>_Edytuj</span>
                                </Localized>
                            </Button>
                        </If>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isLoading) {
        return <>
            <LoadingContainer/>
        </>
    }

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <UserRequestsFilterPanel filters={filters} onFilterChange={setFilters} reload={reload} />
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 195px)' }}
                    rows={userRequestsFiltered}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'userName', sort: 'asc' }] }
                    }}
                />
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="lg" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    );
}
